import { useState, useEffect } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { X, FileWarning, Loader2, Download, MapPin, Calendar, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const NoticeDialog = ({ inspectionId, defaultInspectionDate, open, onClose }) => {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [propertyAddress, setPropertyAddress] = useState("");
  const [dateOfNotice, setDateOfNotice] = useState(today);
  const [dateOfInspection, setDateOfInspection] = useState(
    defaultInspectionDate ? defaultInspectionDate.slice(0, 10) : today
  );
  const [busy, setBusy] = useState(false);
  const [photoReport, setPhotoReport] = useState(null);   // latest generated version meta
  const [attachPr, setAttachPr] = useState(false);
  const [templates, setTemplates] = useState([]);          // published notice templates (builtin first)
  const [templateId, setTemplateId] = useState("builtin_pool_notice");
  const [aiState, setAiState] = useState("idle");           // idle | drafting | ready
  const [aiFields, setAiFields] = useState({ identified_breaches: "", required_actions: "", relevant_legislation: "", compliance_due_date: "" });

  useEffect(() => {
    if (defaultInspectionDate) setDateOfInspection(defaultInspectionDate.slice(0, 10));
  }, [defaultInspectionDate]);

  useEffect(() => {
    if (!open) return;
    api.get(`/inspections/${inspectionId}/photo-report`).then((r) => {
      const vs = r.data?.versions || [];
      setPhotoReport(vs.length ? vs[vs.length - 1] : null);
    }).catch(() => setPhotoReport(null));
    api.get("/doc-builders/templates", { params: { module: "pool_spa", doc_type: "notice" } })
      .then((r) => setTemplates((r.data || []).filter((t) => t.builtin || t.status === "published")))
      .catch(() => setTemplates([]));
  }, [open, inspectionId]);

  if (!open) return null;

  const missingProfile = !user?.bdc_number || !user?.mobile;

  const selectedTpl = templates.find((t) => t.id === templateId);
  const isCustomTpl = !!selectedTpl && !selectedTpl.builtin;

  const close = () => { if (!busy) { onClose(); } };

  const draftWithAI = async () => {
    setAiState("drafting");
    try {
      const kick = await api.post(`/inspections/${inspectionId}/ai-draft-notice`);
      const taskId = kick.data.task_id;
      const started = Date.now();
      while (Date.now() - started < 100000) {
        await new Promise((r) => setTimeout(r, 2500));
        const poll = await api.get(`/inspections/ai-draft-notice/${taskId}`);
        if (poll.data.status === "ready") {
          const r = poll.data.result || {};
          let due = "";
          const days = parseInt(r.recommended_timeframe_days, 10);
          if (days > 0) {
            const d = new Date(`${dateOfNotice}T12:00:00`);
            d.setDate(d.getDate() + days);
            due = d.toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
          }
          setAiFields({
            identified_breaches: r.identified_breaches || "",
            required_actions: r.required_actions || "",
            relevant_legislation: r.relevant_legislation || "",
            compliance_due_date: due,
          });
          setAiState("ready");
          toast.success("Poolie drafted the notice sections — review and edit before generating");
          return;
        }
        if (poll.data.status === "failed") throw new Error(poll.data.error || "AI drafting failed");
      }
      throw new Error("AI drafting timed out");
    } catch (e) {
      setAiState("idle");
      toast.error(e?.response?.data?.detail || e.message || "AI could not draft the sections — you can still generate normally");
    }
  };

  const generate = async () => {
    if (!propertyAddress.trim()) {
      toast.error("Please enter the property address");
      return;
    }
    setBusy(true);
    try {
      const overrides = Object.fromEntries(Object.entries(aiFields).filter(([, v]) => v && v.trim()));
      const token = localStorage.getItem("psc_token");
      const res = await fetch(`${API}/inspections/${inspectionId}/notice-pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          property_address: propertyAddress.trim(),
          // append local-noon to avoid UTC-offset date shifting
          date_of_notice: new Date(`${dateOfNotice}T12:00:00`).toISOString(),
          date_of_inspection: new Date(`${dateOfInspection}T12:00:00`).toISOString(),
          attach_photo_report: attachPr,
          template_id: templateId,
          ctx_overrides: isCustomTpl && Object.keys(overrides).length ? overrides : undefined,
        }),
      });
      if (!res.ok) throw new Error("Notice generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `notice-of-non-compliance-${inspectionId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Notice generated");
      onClose();
    } catch (e) {
      toast.error("Could not generate the Notice");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="notice-dialog-overlay">
      <div className="bg-white border-2 border-slate-900 w-full max-w-lg shadow-[8px_8px_0px_0px_rgba(15,23,42,0.2)]" onClick={(e)=>e.stopPropagation()} data-testid="notice-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#1E1B4B] text-white flex items-center justify-center"><FileWarning size={18} strokeWidth={2.5}/></div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Generate</p>
              <h3 className="font-black text-slate-900 leading-tight">Notice of Non-Compliance</h3>
            </div>
          </div>
          <button onClick={close} data-testid="notice-dialog-close" disabled={busy}
            className="w-9 h-9 border-2 border-slate-300 flex items-center justify-center hover:bg-slate-100 disabled:opacity-40"><X size={18} strokeWidth={2.5}/></button>
        </div>

        <div className="p-6 space-y-4">
          {missingProfile && (
            <div className="border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="notice-profile-warning">
              Your certifier profile is incomplete — the Notice will be missing your <strong>BDC number</strong> or <strong>mobile</strong>.
              <Link to="/settings" className="ml-1 underline font-bold" onClick={close}>Fill in your profile</Link>.
            </div>
          )}

          {templates.length > 0 && (
            <div data-testid="notice-template-picker">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
                <FileWarning size={13} strokeWidth={2.5}/>Notice template
              </span>
              <div className="space-y-1.5 max-h-44 overflow-auto">
                {templates.map((t) => (
                  <label key={t.id} className={`flex items-start gap-2.5 border-2 p-2.5 cursor-pointer ${templateId === t.id ? "border-[#1E1B4B] bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}
                    data-testid={`notice-template-option-${t.id}`}>
                    <input type="radio" name="notice-template" checked={templateId === t.id}
                      onChange={() => setTemplateId(t.id)} className="mt-0.5 h-4 w-4 accent-[#1E1B4B]"/>
                    <span className="text-sm text-slate-900 leading-snug">
                      <strong>{t.name}</strong>
                      {t.builtin
                        ? <span className="ml-1.5 px-1.5 py-0.5 bg-teal-50 border border-teal-300 text-teal-800 text-[10px] font-black uppercase">Built-in</span>
                        : <span className="ml-1.5 px-1.5 py-0.5 bg-slate-100 border border-slate-300 text-slate-700 text-[10px] font-black uppercase">Custom · V{t.published_version}</span>}
                      {t.description && <span className="block text-[11px] text-slate-500 mt-0.5">{t.description.length > 110 ? t.description.slice(0, 110) + "…" : t.description}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {isCustomTpl && (
            <div className="border-2 border-teal-300 bg-teal-50/50 p-3 space-y-3" data-testid="notice-ai-draft-panel">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-teal-800">
                  <Sparkles size={13} strokeWidth={2.5}/>AI-drafted sections
                </span>
                <button onClick={draftWithAI} disabled={aiState === "drafting" || busy} data-testid="notice-ai-draft-button"
                  className="h-9 px-3 inline-flex items-center gap-1.5 border-2 border-teal-600 bg-white text-teal-700 font-bold text-xs hover:bg-teal-50 disabled:opacity-60">
                  {aiState === "drafting" ? <><Loader2 size={13} className="animate-spin"/>Poolie is drafting…</>
                    : <><Sparkles size={13}/>{aiState === "ready" ? "Re-draft from findings" : "Draft from checklist findings"}</>}
                </button>
              </div>
              {aiState === "idle" && (
                <p className="text-[11px] text-teal-900/80 leading-relaxed">
                  Poolie reads your non-compliant checklist items and pre-drafts the <strong>identified breaches</strong>,{" "}
                  <strong>required works</strong> and <strong>legislation</strong> sections in statutory language — you review and edit before generating.
                </p>
              )}
              {aiState === "ready" && (
                <div className="space-y-2.5">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-teal-800">Identified breaches</span>
                    <textarea rows={4} value={aiFields.identified_breaches} data-testid="notice-ai-breaches"
                      onChange={(e) => setAiFields({ ...aiFields, identified_breaches: e.target.value })}
                      className="mt-1 w-full border-2 border-teal-200 bg-white text-slate-900 p-2 text-[13px] leading-snug focus:border-teal-600 focus:outline-none"/>
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-teal-800">Required works</span>
                    <textarea rows={4} value={aiFields.required_actions} data-testid="notice-ai-works"
                      onChange={(e) => setAiFields({ ...aiFields, required_actions: e.target.value })}
                      className="mt-1 w-full border-2 border-teal-200 bg-white text-slate-900 p-2 text-[13px] leading-snug focus:border-teal-600 focus:outline-none"/>
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-teal-800">Relevant legislation</span>
                    <textarea rows={2} value={aiFields.relevant_legislation} data-testid="notice-ai-legislation"
                      onChange={(e) => setAiFields({ ...aiFields, relevant_legislation: e.target.value })}
                      className="mt-1 w-full border-2 border-teal-200 bg-white text-slate-900 p-2 text-[13px] leading-snug focus:border-teal-600 focus:outline-none"/>
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-teal-800">Compliance due date</span>
                    <input value={aiFields.compliance_due_date} data-testid="notice-ai-due-date"
                      onChange={(e) => setAiFields({ ...aiFields, compliance_due_date: e.target.value })}
                      className="mt-1 w-full h-10 border-2 border-teal-200 bg-white text-slate-900 px-2 text-[13px] focus:border-teal-600 focus:outline-none"/>
                  </label>
                  <p className="text-[10px] text-teal-900/70">These sections replace the auto-generated {"{{identified_breaches}}"}, {"{{required_actions}}"}, {"{{relevant_legislation}}"} and {"{{compliance_due_date}}"} fields in your template.</p>
                </div>
              )}
            </div>
          )}

          <label className="block">
            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
              <MapPin size={13} strokeWidth={2.5}/>Property address *
            </span>
            <input data-testid="notice-property-input" value={propertyAddress} onChange={(e)=>setPropertyAddress(e.target.value)}
              placeholder="e.g. 9 Cobble Circuit, West Hoxton NSW 2171"
              className="h-12 w-full px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"/>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
                <Calendar size={13} strokeWidth={2.5}/>Date of inspection
              </span>
              <input data-testid="notice-date-inspection" type="date" value={dateOfInspection}
                onChange={(e)=>setDateOfInspection(e.target.value)}
                className="h-12 w-full px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"/>
            </label>
            <label className="block">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
                <Calendar size={13} strokeWidth={2.5}/>Date of notice
              </span>
              <input data-testid="notice-date-notice" type="date" value={dateOfNotice}
                onChange={(e)=>setDateOfNotice(e.target.value)}
                className="h-12 w-full px-3 border-2 border-slate-300 bg-white focus:border-[#1E1B4B] focus:outline-none text-base"/>
            </label>
          </div>

          <p className="text-xs text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 p-3">
            The Notice will include a branded cover page, AS 1926.1-2012 reference page, a numbered list of every item you marked
            <strong className="text-red-700"> NO</strong>, and a dedicated photo page (with caption + remediation steps) for every photo attached to a non-compliance.
          </p>

          {photoReport && (
            <label className="flex items-center gap-2.5 border-2 border-teal-200 bg-teal-50 p-3 cursor-pointer" data-testid="notice-attach-photo-report">
              <input type="checkbox" checked={attachPr} onChange={(e) => setAttachPr(e.target.checked)}
                className="h-5 w-5 accent-[#00F2C3]" data-testid="notice-attach-photo-report-checkbox"/>
              <span className="text-sm text-slate-800">
                <strong>Attach Photo Report</strong> (v{photoReport.version}, {(photoReport.generated_at || "").slice(0, 10)}) — appended to the end of the Notice PDF
              </span>
            </label>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={generate} disabled={busy} data-testid="notice-generate-button"
              className="flex-1 h-12 inline-flex items-center justify-center gap-2 bg-[#1E1B4B] text-white font-bold border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60">
              {busy ? <><Loader2 size={16} className="animate-spin"/>Generating…</> : <><Download size={16} strokeWidth={2.5}/>Generate &amp; Download</>}
            </button>
            <button onClick={close} disabled={busy} data-testid="notice-cancel-button"
              className="h-12 px-5 bg-white border-2 border-slate-900 text-slate-900 font-bold hover:bg-slate-100 disabled:opacity-60">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoticeDialog;
